'use client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { forwardRef, useEffect, useRef, useState } from 'react';
import type { PhoneInputProps, CountryData } from 'react-international-phone';
import { PhoneInput, defaultCountries } from 'react-international-phone';
import { TiArrowSortedDown } from 'react-icons/ti';
import './phone-input-styles.css';

export const PhoneInputField = forwardRef<
  HTMLInputElement,
  PhoneInputProps & React.AriaAttributes & { id?: string; variant?: 'outlined' | 'default' }
>(function PhoneInputField({ className, variant, id, 'aria-describedby': describedBy, 'aria-invalid': invalid, 'aria-label': label, 'aria-labelledby': labelledBy, ...props }, ref) {
  const [focused, setFocused] = useState(false);
  const phoneInputRef = useRef<HTMLDivElement>(null);

  const customCountries = defaultCountries.map(country => {
    if (country[0] === 'Slovakia') {
      return ['Slovakia', 'sk', '421', '... ... ...'] as CountryData;
    }
    return country;
  });

  const locale = useLocaleStore(s => s.currentLocale);

  const defaultCountryFromLocale = () => {
    if (locale === 'cs') {
      return 'cz';
    }

    return locale;
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const current = phoneInputRef.current as any;

      if (current && !current.contains(event.target)) {
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [phoneInputRef]);

  return (
    <div
      ref={phoneInputRef as any}
      className={cn(
        'relative h-12 sm:h-[52px] w-full rounded-[12px] border border-border bg-transparent',
        className,
      )}
    >
      <PhoneInput
        ref={ref}
        defaultCountry={defaultCountryFromLocale()}
        name="phone"
        countries={customCountries}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputClassName={cn('text-sm lg:text-base text-secondary', props.inputClassName)}
        inputStyle={{
          height: '100%',
          paddingLeft: '20px',
          width: '100%',
          border: 'none',
          borderEndEndRadius: 0,
          borderStartEndRadius: 0,
          backgroundColor: 'transparent',
        }}
        countrySelectorStyleProps={{
          buttonClassName: '',
          buttonStyle: {
            height: '100%',
            border: 'none',
            paddingLeft: '12px',
            paddingRight: '28px',
            backgroundColor: 'transparent',
          },
          dropdownArrowStyle: { display: 'none' },
        }}
        {...props}
        inputProps={{
          ...props.inputProps,
          ...(id ? { id } : {}),
          ...(describedBy ? { 'aria-describedby': describedBy } : {}),
          ...(invalid !== undefined ? { 'aria-invalid': invalid } : {}),
          ...(label ? { 'aria-label': label } : {}),
          ...(labelledBy ? { 'aria-labelledby': labelledBy } : {}),
        }}
      />
      <span className="pointer-events-none absolute left-[44px] top-1/2 -translate-y-1/2">
        <TiArrowSortedDown size={20} className="text-primary" />
      </span>
    </div>
  );
});

PhoneInputField.displayName = 'PhoneInputField';
